/**
 * Промпты и схемы ответов Claude — перенос из прототипа (runParse, два прохода scanMail, runDraft).
 * Структурированные ответы — через инструмент со строгой JSON-схемой (strict tool use).
 * При изменении текста промпта — повышайте PROMPT_VERSION (пишется в журнал ai_calls).
 */
import { weekday, type DateStr } from './dates';
import {
  CHANNELS,
  FORMATS,
  isOneOf,
  LANG_LABELS,
  SOURCES,
  type Channel,
  type Format,
  type LetterLang,
  type Source,
} from './model';
import { STAGE_KEYS, normStage, stageTitle, type Stage } from './stages';
import { isDate, isTime } from './dates';

export const PROMPT_VERSION = 'v1';
/** Вход обрезается до этого размера с пометкой (SPEC: «вход до 14 000 символов»). */
export const MAX_INPUT_CHARS = 14_000;

const WEEKDAYS = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'];
export const todayLine = (today: DateStr) => `Сегодня ${today}, ${WEEKDAYS[weekday(today)]}.`;

export function clip(text: string, max = MAX_INPUT_CHARS): { text: string; truncated: boolean } {
  if (text.length <= max) return { text, truncated: false };
  return { text: text.slice(0, max) + '\n[…текст обрезан…]', truncated: true };
}

export interface TrackerRow {
  id: string;
  company: string;
  role: string;
  stage: Stage;
}

/** Список до 80 вакансий «id | компания | роль | статус», чтобы Claude находил существующую. */
export function trackerList(rows: readonly TrackerRow[]): string {
  return (
    rows
      .slice(0, 80)
      .map((a) => `${a.id} | ${a.company} | ${a.role} | ${stageTitle(a.stage)}`)
      .join('\n') || '(пусто)'
  );
}

/* ---------- JSON-схемы ---------- */

type Json = Record<string, unknown>;
const str = (description?: string): Json =>
  description ? { type: 'string', description } : { type: 'string' };
const en = (values: readonly string[], description?: string): Json => ({
  type: 'string',
  enum: [...values],
  ...(description ? { description } : {}),
});
const obj = (properties: Record<string, Json>): Json => ({
  type: 'object',
  properties,
  required: Object.keys(properties),
  additionalProperties: false,
});

const CONTACT = obj({
  name: str(),
  role: str('HR, рекрутер, тимлид…'),
  email: str(),
  linkedin: str(),
  telegram: str(),
  phone: str(),
});

const OFFER = {
  anyOf: [
    { type: 'null' },
    obj({
      salary: str('число в тыс. € gross в год, например "72"'),
      bonus: str(),
      format: en([...FORMATS, '']),
      contract: str(),
      start: str('YYYY-MM-DD или пустая строка'),
      deadline: str('YYYY-MM-DD или пустая строка'),
      benefits: str(),
    }),
  ],
};

const STAGE_OR_EMPTY = en([...STAGE_KEYS, '']);
const FLOW_STAGE = en([
  'applied',
  'screening',
  'screening_wait',
  'interview',
  'interview_wait',
  'final',
  'final_wait',
  'offer',
  'rejected',
  '',
]);

export interface ToolSpec {
  name: string;
  description: string;
  input_schema: Json;
}

export const PARSE_TOOL: ToolSpec = {
  name: 'save_parsed',
  description: 'Сохранить извлечённые из текста данные о вакансии или сообщении.',
  input_schema: obj({
    type: en(['vacancy', 'message'], 'vacancy — описание вакансии; message — письмо или сообщение'),
    matchId: str('id существующей вакансии, если текст явно про неё, иначе пустая строка'),
    company: str(),
    role: str(),
    url: str('ссылка на вакансию, если есть'),
    source: en(SOURCES),
    location: str(),
    format: en([...FORMATS, '']),
    salary: str('вилка как в тексте'),
    contact: CONTACT,
    channel: en(CHANNELS.filter((c) => c !== 'System')),
    direction: en(['in', 'out'], 'in — написали пользователю, out — пишет он'),
    summary: str('1–2 предложения по-русски: суть; для вакансии — уровень, стек, ключевые требования'),
    stage: STAGE_OR_EMPTY,
    rejectionReason: str(),
    nextStep: str('что пользователю сделать дальше, если это следует из текста'),
    nextDate: str('YYYY-MM-DD или пустая строка'),
    nextTime: str('HH:MM или пустая строка'),
    offer: OFFER,
  }),
};

export const MAIL_CLASSIFY_TOOL: ToolSpec = {
  name: 'classify_emails',
  description: 'Классифицировать письма: по одному элементу на каждое письмо.',
  input_schema: obj({
    items: {
      type: 'array',
      items: obj({
        i: { type: 'integer' },
        relevant: { type: 'boolean' },
        kind: en(['applied', 'viewed', 'message', 'screening', 'interview', 'rejection', 'offer', 'other']),
        company: str(),
        role: str(),
        matchId: str('id вакансии из трекера или пустая строка'),
        summary: str('одно предложение по-русски: что произошло'),
        stage: FLOW_STAGE,
        needsBody: { type: 'boolean' },
      }),
    },
  }),
};

export const MAIL_EXTRACT_TOOL: ToolSpec = {
  name: 'extract_emails',
  description: 'Извлечь детали из полных текстов писем: по одному элементу на письмо, в том же порядке.',
  input_schema: obj({
    items: {
      type: 'array',
      items: obj({
        j: { type: 'integer' },
        matchId: str(),
        company: str(),
        role: str(),
        url: str(),
        location: str(),
        format: en([...FORMATS, '']),
        salary: str(),
        contact: CONTACT,
        summary: str('1–2 предложения по-русски: суть и о чём договориться'),
        stage: FLOW_STAGE,
        rejectionReason: str(),
        nextStep: str(),
        nextDate: str('YYYY-MM-DD или пустая строка'),
        nextTime: str('HH:MM или пустая строка'),
        offer: OFFER,
      }),
    },
  }),
};

/* ---------- промпты ---------- */

export function parsePrompt(o: {
  text: string;
  today: DateStr;
  tracker: string;
  targetId?: string | null;
}): string {
  const { text } = clip(o.text);
  return `Ты — помощник в трекере откликов на вакансии. Пользователь вставил текст: это либо описание вакансии (пост из Telegram, LinkedIn, Glassdoor, письмо-подтверждение отклика), либо сообщение/письмо от рекрутера или компании (или его собственное сообщение им).
${todayLine(o.today)} Относительные даты («завтра», «в четверг», «next Monday») переводи в YYYY-MM-DD от сегодняшней даты.
${o.targetId ? `Пользователь открыл вакансию с id ${o.targetId} — скорее всего текст относится к ней.\n` : ''}Вакансии в трекере (id | компания | роль | статус):
${o.tracker}

Извлеки данные и вызови инструмент ${PARSE_TOOL.name}. Поля:
- matchId — id существующей вакансии, если текст явно про неё (та же компания; роль совпадает или не указана);
- stage — какой статус следует из текста: подтверждение отклика → applied, рекрутер вышел на связь или зовёт на звонок с HR → screening, HR-звонок прошёл и ждём решения → screening_wait, техническое интервью → interview, техническое прошло и ждём итогов → interview_wait, финальное → final, финальное прошло и ждём решения → final_wait, оффер → offer, отказ → rejected; пустая строка, если неясно; письмо самого кандидата (уточняет статус отклика, напоминает о себе) статус не меняет — пустая строка; «ждём решения» — только если этап (звонок, интервью) действительно прошёл, а не после отправки отклика;
- offer — null, если в тексте нет оффера.
Не выдумывай: если данных нет — пустая строка.

Текст:
"""
${text}
"""`;
}

export interface MailItemInput {
  i: number;
  from: string;
  subject: string;
  snippet: string;
  date: string;
  sent: boolean;
}

export function mailClassifyPrompt(o: {
  about: string;
  today: DateStr;
  tracker: string;
  items: readonly MailItemInput[];
}): string {
  return `Ты разбираешь письма из Gmail для трекера откликов на вакансии. Кандидат: ${o.about || 'QA-инженер'}. ${todayLine(o.today)}
Вакансии в трекере (id | компания | роль | статус):
${o.tracker}

Письма (i, from, subject, snippet, date, sent = исходящее письмо кандидата):
${JSON.stringify(o.items)}

Вызови инструмент ${MAIL_CLASSIFY_TOOL.name}, по элементу на каждое письмо:
- relevant — true, если письмо про отклики самого кандидата (подтверждение отклика, сообщение рекрутера или HR, приглашение, отказ, оффер, переписка по конкретной вакансии); подборки вакансий, рассылки, реклама, уведомления о просмотре профиля — false;
- matchId — id вакансии из трекера, если письмо явно про неё, иначе пустая строка;
- stage — статус, который следует из письма, или пустая строка;
- needsBody — true, если для деталей (дата интервью, контакт, причина отказа, условия оффера) нужно полное письмо.`;
}

export interface MailBodyInput {
  from: string;
  date: string;
  subject: string;
  body: string;
}

export function mailExtractPrompt(o: {
  today: DateStr;
  tracker: string;
  letters: readonly MailBodyInput[];
}): string {
  return `Ты помощник трекера откликов на вакансии. ${todayLine(o.today)} Относительные даты переводи в YYYY-MM-DD.
Вакансии в трекере (id | компания | роль | статус):
${o.tracker}

Письма:
${o.letters.map((l, j) => `### ${j}\nFrom: ${l.from}\nDate: ${l.date}\nSubject: ${l.subject}\n${l.body}`).join('\n\n')}

Вызови инструмент ${MAIL_EXTRACT_TOOL.name}, по элементу на каждое письмо в том же порядке (j — номер письма).
contact — живой человек из письма (рекрутер, HR); noreply-адреса не указывай. offer — null, если оффера нет.
Не выдумывай: если данных нет — пустая строка.`;
}

/* ---------- черновики ---------- */

export const DRAFT_KINDS = [
  'reply',
  'followup',
  'thanks',
  'prep',
  'accept',
  'negotiate',
  'decline',
  'feedback',
] as const;
export type DraftKind = (typeof DRAFT_KINDS)[number];

export const DRAFTS: Record<DraftKind, { title: string; prompt: string }> = {
  reply: {
    title: 'Ответить на последнее',
    prompt: 'ответ на последнее входящее сообщение от компании или рекрутера',
  },
  followup: {
    title: 'Напомнить о себе',
    prompt: 'короткое вежливое напоминание о себе после отклика без ответа, с живым интересом к роли',
  },
  thanks: {
    title: 'Спасибо после интервью',
    prompt: 'благодарность после интервью; коротко подчеркни 1–2 сильные стороны кандидата по теме разговора',
  },
  accept: {
    title: 'Принять оффер',
    prompt:
      'принятие оффера: подтверди ключевые условия и дату выхода, спроси о следующих шагах (контракт, документы, онбординг)',
  },
  negotiate: {
    title: 'Торговаться',
    prompt:
      'вежливое контрпредложение по офферу: благодарность, интерес к роли, аргументированная просьба улучшить условия с конкретной цифрой',
  },
  prep: {
    title: 'План подготовки',
    prompt:
      'план подготовки к следующему этапу собеседования: ключевые темы по стеку вакансии, 8–10 вероятных вопросов для QA-инженера этого уровня, 3 истории из опыта в формате STAR (с пометками [вставить детали] там, где нужны реальные факты кандидата) и 4–5 вопросов к команде',
  },
  feedback: {
    title: 'Попросить обратную связь',
    prompt:
      'короткая вежливая просьба о обратной связи после отказа: что можно было сделать лучше; с благодарностью за время',
  },
  decline: {
    title: 'Вежливо отказаться',
    prompt: 'вежливый отказ от оффера или продолжения процесса, сохраняя хорошие отношения на будущее',
  },
};

export interface DraftContext {
  kind: DraftKind;
  lang: LetterLang;
  extra: string;
  name: string;
  about: string;
  company: string;
  role: string;
  source: string;
  stage: Stage;
  nextStep: string;
  nextDate: string | null;
  nextTime: string | null;
  conditions: string;
  notes: string;
  contact: { name: string; role: string; email: string } | null;
  /** Последние записи истории (без системных), старые сверху. */
  history: { date: string; direction: 'in' | 'out'; channel: string; text: string }[];
  offer: string;
  targetSalaryK: number | null;
  minSalaryK: number | null;
  availableFrom: string | null;
  noticePeriod: string;
}

/** Промпт черновика — как runDraft() в прототипе. */
export function draftPrompt(d: DraftContext): string {
  const log =
    d.history
      .slice(-8)
      .map((l) => `${l.date} ${l.direction === 'in' ? 'от них' : 'от кандидата'} (${l.channel}): ${l.text}`)
      .join('\n') || '(записей нет)';
  return [
    `Напиши ${DRAFTS[d.kind].prompt}.`,
    `Язык: ${LANG_LABELS[d.lang]}.`,
    d.kind === 'prep'
      ? 'Формат: план для самого кандидата на указанном языке, списком, до 400 слов, без темы и подписи.'
      : d.contact?.email
        ? 'Формат: письмо. Первая строка — "Subject: …", затем пустая строка и текст (до 170 слов).'
        : 'Формат: сообщение в LinkedIn или мессенджере, без темы, до 110 слов.',
    `Кандидат: ${d.name || 'имя не указано — в подписи оставь [Имя]'}. О кандидате: ${d.about || '—'}.`,
    `Компания: ${d.company || '—'}. Роль: ${d.role || '—'}. Источник: ${d.source || '—'}. Текущий статус: ${stageTitle(d.stage)}.`,
    d.nextStep || d.nextDate
      ? `Следующий шаг: ${d.nextStep || '—'}${d.nextDate ? ', ' + d.nextDate : ''}${d.nextTime ? ' ' + d.nextTime : ''}.`
      : '',
    d.conditions ? `Условия вакансии: ${d.conditions}.` : '',
    d.kind === 'prep' && d.notes ? `Заметки кандидата о вакансии: ${d.notes.slice(0, 1500)}` : '',
    `Контакт: ${d.contact?.name || 'имя неизвестно'}${d.contact?.role ? ' (' + d.contact.role + ')' : ''}.`,
    `История общения:\n${log}`,
    ['accept', 'negotiate', 'decline'].includes(d.kind) ? `Оффер: ${d.offer || '—'}.` : '',
    d.kind === 'negotiate'
      ? `Цель кандидата: ${d.targetSalaryK ?? 'не указана'} тыс. €, минимум: ${d.minSalaryK ?? 'не указан'}. Попроси конкретную цифру не выше цели; аргументируй опытом, без выдуманных фактов.`
      : '',
    d.kind === 'accept'
      ? `Возможная дата выхода: ${d.availableFrom || 'не указана'}. Срок уведомления на текущей работе: ${d.noticePeriod || 'не указан'}.`
      : '',
    d.extra ? `Учесть: ${d.extra}` : '',
    'Пиши естественно и конкретно, без канцелярита, лести и шаблонных фраз. Не выдумывай факты. Верни только текст сообщения.',
  ]
    .filter(Boolean)
    .join('\n');
}

/** «Subject: …» в первой строке → тема и тело письма. */
export function splitSubject(t: string): { subject: string; body: string } {
  const m = t.match(/^\s*Subject:\s*(.+)\n+/i);
  return m ? { subject: m[1]!.trim(), body: t.slice(m[0].length) } : { subject: '', body: t };
}

/* ---------- нормализация ответов ---------- */

export interface ParsedOffer {
  salaryK: number | null;
  bonus: string;
  format: Format | null;
  contract: string;
  startDate: string | null;
  deadline: string | null;
  benefits: string;
}

export interface ParsedText {
  type: 'vacancy' | 'message';
  matchId: string | null;
  company: string;
  role: string;
  url: string;
  source: Source;
  location: string;
  format: Format | null;
  salary: string;
  contact: {
    name: string;
    role: string;
    email: string;
    linkedinUrl: string;
    telegram: string;
    phone: string;
  };
  channel: Channel;
  direction: 'in' | 'out';
  summary: string;
  stage: Stage | null;
  rejectionReason: string;
  nextStep: string;
  nextDate: string | null;
  nextTime: string | null;
  offer: ParsedOffer | null;
}

const s = (v: unknown) => (v == null ? '' : String(v).trim());

export function normOffer(o: unknown): ParsedOffer | null {
  if (!o || typeof o !== 'object') return null;
  const r = o as Record<string, unknown>;
  const n = parseFloat(
    s(r.salary)
      .replace(/[^\d.,]/g, '')
      .replace(',', '.'),
  );
  const salaryK = isFinite(n) && n > 0 ? Math.round((n > 1000 ? n / 1000 : n) * 10) / 10 : null;
  const offer: ParsedOffer = {
    salaryK,
    bonus: s(r.bonus),
    format: isOneOf(FORMATS, r.format) ? r.format : null,
    contract: s(r.contract),
    startDate: isDate(r.start) ? r.start : null,
    deadline: isDate(r.deadline) ? r.deadline : null,
    benefits: s(r.benefits),
  };
  return Object.values(offer).some((v) => v != null && v !== '') ? offer : null;
}

export function normContact(c: unknown) {
  const r = (c && typeof c === 'object' ? c : {}) as Record<string, unknown>;
  return {
    name: s(r.name),
    role: s(r.role),
    email: s(r.email),
    linkedinUrl: s(r.linkedin ?? r.linkedinUrl),
    telegram: s(r.telegram),
    phone: s(r.phone),
  };
}

/** Ответ разбора → проверенные поля (как normParsed() в прототипе). Неизвестный matchId отбрасывается. */
export function normParsed(r: Record<string, unknown>, knownIds: ReadonlySet<string>): ParsedText {
  const id = s(r.matchId);
  return {
    type: r.type === 'vacancy' ? 'vacancy' : 'message',
    matchId: id && knownIds.has(id) ? id : null,
    company: s(r.company),
    role: s(r.role),
    url: s(r.url),
    source: isOneOf(SOURCES, r.source) ? r.source : 'Other',
    location: s(r.location),
    format: isOneOf(FORMATS, r.format) ? r.format : null,
    salary: s(r.salary),
    contact: normContact(r.contact),
    channel: isOneOf(CHANNELS, r.channel) && r.channel !== 'System' ? r.channel : 'Other',
    direction: r.direction === 'out' ? 'out' : 'in',
    summary: s(r.summary),
    stage: normStage(r.stage),
    rejectionReason: s(r.rejectionReason),
    nextStep: s(r.nextStep),
    nextDate: isDate(r.nextDate) ? r.nextDate : null,
    nextTime: isTime(r.nextTime) ? r.nextTime : null,
    offer: normOffer(r.offer),
  };
}

/* ---------- поиск контакта (HR, рекрутер) в интернете ---------- */

export const CONTACT_SEARCH_TOOL: ToolSpec = {
  name: 'save_contacts',
  description:
    'Сохранить найденные контакты по вакансии: людей, отвечающих за найм, и контакт компании для откликов.',
  input_schema: obj({
    people: {
      type: 'array',
      items: obj({
        name: str('имя и фамилия; пусто — контакт компании (почта или страница для откликов)'),
        role: str('должность: Recruiter, Talent Acquisition, HR, Hiring Manager…'),
        linkedinUrl: str('ссылка на профиль LinkedIn, если найдена'),
        email: str('рабочая почта, только если опубликована в источнике; не угадывать'),
        telegram: str('Telegram, только если опубликован как рабочий контакт'),
        sourceUrl: str('страница, где найден контакт'),
        why: str('одно предложение по-русски: почему это нужный человек'),
      }),
    },
    note: str('коротко по-русски: что искали и что не нашлось'),
  }),
};

export interface ContactSearchInput {
  company: string;
  role: string;
  location: string;
  url: string;
  /** Описание вакансии или заметки — начало. */
  details: string;
}

export function contactSearchPrompt(o: ContactSearchInput): string {
  return `Пользователь откликнулся на вакансию и хочет написать человеку, который отвечает за найм.
Вакансия: ${o.role || 'роль не указана'} в компании ${o.company || 'не указана'}${o.location ? `, ${o.location}` : ''}.
${o.url ? `Ссылка на вакансию: ${o.url}\n` : ''}${o.details ? `Из описания:\n"""\n${o.details.slice(0, 1500)}\n"""\n` : ''}
Найди в интернете (инструмент web_search) 1–3 подходящих контакта:
- рекрутер, Talent Acquisition или HR этой компании, лучше всего — тот, кто ведёт вакансии QA/инженерные в этой стране или регионе; либо hiring manager (руководитель QA/инжиниринга);
- если вакансию разместило кадровое агентство — рекрутер агентства;
- контакт компании для откликов (careers@…, страница «Карьера») — отдельной записью без имени.
Ищи, например: «<компания> recruiter LinkedIn», «<компания> talent acquisition», «site:linkedin.com/in <компания> recruiter», страницу вакансии и страницу «Карьера».

Правила:
- только рабочие данные, опубликованные открыто: имя, должность, профиль LinkedIn, рабочая почта или Telegram, если они указаны в источнике;
- не угадывай адреса по шаблону (имя.фамилия@…) и не придумывай людей; личные телефоны и адреса не нужны;
- у каждого контакта — sourceUrl, где он найден; человек должен работать в этой компании сейчас;
- не нашлось — пустой список и объяснение в note.
В конце обязательно вызови инструмент ${CONTACT_SEARCH_TOOL.name}.`;
}
